import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Skeleton, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import ActionCard from "./ActionCard";
import TasksCard from "./TasksCard";
import ReviewPatientsModal from "./ReviewPatientsModal";
import SuccessAnimation from "./SuccessAnimation";

import {
  getPaytientEligiblePatientsAPI,
  bulkSendPaytientAPI,
  getExpressEnrollInvitesEligiblePatientsAPI,
  bulkSendExpressEnrollInvitesAPI,
  getPendingTasksCountsAPI,
  checkShouldPromptForApprovalAPI,
  createAutomaticApprovalAPI,
  getAutomaticApprovalByTypeAPI
} from "../../api/api";
import { selectUser, selectFormPopups } from "../../store/selector";
import { PAYTIENT_PRIMARY, PRIMARY_600 } from "../../constant/colors";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import ACTIONS from "../../store/action";
import { ACTION_TYPES, API_ACTION_TYPES, SEND_SOURCES } from "./constants";
import { usePendingActionsPendoTracking } from "../../utils/pendo/usePendingActionsPendoTracking";
import { useBoxImpressionTracking } from "../../utils/pendo/useBoxImpressionTracking";
import { ContentTypes, LayoutTypes } from "../../utils/pendo/useStandardizedTracking";
import { useGetSetting } from "../../hooks/getSetting";
import "./PendingActions.less";

// Track previous popup state to detect when Tasks popup closes
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

/**
 * Map UI action type to API action type
 */
const getApiActionType = (uiActionType) => {
  switch (uiActionType) {
    case ACTION_TYPES.M3P_ELIGIBLE:
      return API_ACTION_TYPES.PAYTIENT;
    case ACTION_TYPES.EXPRESS_ENROLL_INVITES_ELIGIBLE:
      return API_ACTION_TYPES.EXPRESS_ENROLL_INVITES;
    default:
      return uiActionType;
  }
};

/**
 * Get the correct bulk send API for an action type
 */
const getBulkSendAPI = (uiActionType) => {
  switch (uiActionType) {
    case ACTION_TYPES.EXPRESS_ENROLL_INVITES_ELIGIBLE:
      return bulkSendExpressEnrollInvitesAPI;
    case ACTION_TYPES.M3P_ELIGIBLE:
    default:
      return bulkSendPaytientAPI;
  }
};

const LoadingState = () => (
  <div className="pending-actions-grid">
    {[1, 2, 3].map((item) => (
      <Skeleton.Button key={item} active block style={{ height: 140, borderRadius: 8, minWidth: 280 }} />
    ))}
  </div>
);

const EmptyState = ({ t }) => (
  <div className="pending-actions-empty">
    <CheckCircleOutlined className="empty-icon" />
    <div className="empty-title">{t("pending_actions.empty_state.title", "All caught up!")}</div>
    <div className="empty-description">
      {t(
        "pending_actions.empty_state.description",
        "No pending actions at the moment. Check back later for new patient actions."
      )}
    </div>
  </div>
);

const PendingActions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const formPopups = useSelector(selectFormPopups);

  // Check if pending actions feature is enabled
  const [isPendingActionsEnabled] = useGetSetting(["pending-actions.enabled"]);

  // Pendo tracking
  const {
    trackSectionImpression,
    trackM3PCardImpression,
    trackSendInvitesClick,
    trackSendInvitesSuccess,
    trackReviewPatientsClick,
    trackTasksCardImpression,
    trackViewTasksClick,
    trackAutoApprovalBannerImpression,
    trackAutoApprovalEnableClick,
    trackAutoApprovalDismissClick,
    trackAutoApprovalEnabled,
    trackTooltipClick
  } = usePendingActionsPendoTracking();
  const [selectedAction, setSelectedAction] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [successAnimation, setSuccessAnimation] = useState({ show: false, actionId: null, count: 0 });
  const cardRefs = useRef({});
  const timeoutRefs = useRef([]);

  // Cleanup timeouts on unmount to prevent memory leaks and state updates on unmounted component
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timerId) => clearTimeout(timerId));
      timeoutRefs.current = [];
    };
  }, []);
  const [paytientData, setPaytientData] = useState({
    patients: [],
    totalCount: 0,
    minThreshold: 5,
    tooltip: ""
  });
  const [expressEnrollInvitesData, setExpressEnrollInvitesData] = useState({
    patients: [],
    totalCount: 0,
    minThreshold: 5,
    tooltip: ""
  });
  const [tasksData, setTasksData] = useState({
    overdueCount: 0,
    pendingTodayCount: 0,
    totalCount: 0
  });

  // Express Enroll Invites card impression tracking via IntersectionObserver
  const expressEnrollInvitesAdditionalInfo = useMemo(
    () => ({
      eligible_count: expressEnrollInvitesData.totalCount,
      total_eligible: expressEnrollInvitesData.totalCount
    }),
    [expressEnrollInvitesData.totalCount]
  );
  const expressEnrollInvitesImpressionRef = useBoxImpressionTracking({
    featureGroup: "pending_actions",
    pageKey: "home",
    contentType: ContentTypes.PATIENT,
    contentKey: "express_enroll_invites_eligible_card",
    layout: LayoutTypes.CARD,
    additionalInfo: expressEnrollInvitesAdditionalInfo,
    enabled: !isLoading && expressEnrollInvitesData.totalCount >= expressEnrollInvitesData.minThreshold
  });

  // Auto-approval state
  const [showAutoApprovalPrompt, setShowAutoApprovalPrompt] = useState(false);
  const [autoApprovalActionType, setAutoApprovalActionType] = useState(null);
  const [hasActiveAutoApproval, setHasActiveAutoApproval] = useState({});
  const [isEnablingAutoApproval, setIsEnablingAutoApproval] = useState(false);
  const [dismissedAutoApproval, setDismissedAutoApproval] = useState({});

  // Track if Tasks popup was open
  const isTasksPopupOpen = formPopups?.visible && formPopups?.contentKey === formPopupsContentKeys.TASKS;
  const wasTasksPopupOpen = usePrevious(isTasksPopupOpen);

  // Check if an action type has active auto-approval
  const checkAutoApprovalStatus = useCallback(async (actionType) => {
    try {
      const response = await getAutomaticApprovalByTypeAPI(actionType);
      if (response?.data?.success) {
        setHasActiveAutoApproval((prev) => ({
          ...prev,
          [actionType]: response.data.data.hasActiveApproval
        }));
        return response.data.data.hasActiveApproval;
      }
    } catch (error) {
      console.error("Error checking auto-approval status:", error);
    }
    return false;
  }, []);

  // Check if we should prompt for auto-approval after send
  const checkShouldPromptForAutoApproval = useCallback(
    async (actionType) => {
      // Don't prompt if already dismissed in this session
      if (dismissedAutoApproval[actionType]) return false;

      try {
        const response = await checkShouldPromptForApprovalAPI(actionType);
        if (response?.data?.success && response.data.data.shouldPrompt) {
          return true;
        }
      } catch (error) {
        console.error("Error checking auto-approval prompt:", error);
      }
      return false;
    },
    [dismissedAutoApproval]
  );

  // Fetch Paytient eligible patients
  const fetchPaytientPatients = useCallback(async () => {
    try {
      const response = await getPaytientEligiblePatientsAPI();
      if (response?.data?.success) {
        setPaytientData({
          patients: response.data.data.patients || [],
          totalCount: response.data.data.totalCount || 0,
          minThreshold: response.data.data.minThreshold || 5,
          tooltip: response.data.data.tooltip || ""
        });
      }
    } catch (error) {
      console.error("Error fetching Paytient eligible patients:", error);
    }
  }, []);

  // Fetch Express Enroll Invites eligible patients
  const fetchExpressEnrollInvitesPatients = useCallback(async () => {
    try {
      const response = await getExpressEnrollInvitesEligiblePatientsAPI();
      if (response?.data?.success) {
        setExpressEnrollInvitesData({
          patients: response.data.data.patients || [],
          totalCount: response.data.data.totalCount || 0,
          minThreshold: response.data.data.minThreshold || 5,
          tooltip: response.data.data.tooltip || ""
        });
      }
    } catch (error) {
      console.error("Error fetching Express Enroll Invites eligible patients:", error);
    }
  }, []);

  // Refresh data for a specific action type after send
  const refreshDataForActionType = useCallback(
    async (apiActionType) => {
      if (apiActionType === API_ACTION_TYPES.EXPRESS_ENROLL_INVITES) {
        await fetchExpressEnrollInvitesPatients();
      } else {
        await fetchPaytientPatients();
      }
    },
    [fetchPaytientPatients, fetchExpressEnrollInvitesPatients]
  );

  // Post-send success flow: clear animation, show message, optionally prompt for auto-approval
  const handlePostSendSuccess = useCallback(
    async ({ actionType, sentCount }) => {
      const shouldShowPrompt =
        actionType && !hasActiveAutoApproval[actionType] && (await checkShouldPromptForAutoApproval(actionType));

      if (!shouldShowPrompt) {
        await refreshDataForActionType(actionType);
      }

      setSuccessAnimation({ show: false, actionId: null, count: 0 });
      message.success(t("pending_actions.send_success", `Successfully sent to ${sentCount} patients`));

      if (shouldShowPrompt) {
        setAutoApprovalActionType(actionType);
        setShowAutoApprovalPrompt(true);
        trackAutoApprovalBannerImpression(actionType);
      }
    },
    [
      hasActiveAutoApproval,
      checkShouldPromptForAutoApproval,
      refreshDataForActionType,
      t,
      trackAutoApprovalBannerImpression
    ]
  );

  // Handle enabling auto-approval
  const handleEnableAutoApproval = useCallback(
    async (actionType) => {
      trackAutoApprovalEnableClick(actionType);
      setIsEnablingAutoApproval(true);
      try {
        const response = await createAutomaticApprovalAPI(actionType, {});
        if (response?.data?.success) {
          trackAutoApprovalEnabled(actionType);
          setHasActiveAutoApproval((prev) => ({
            ...prev,
            [actionType]: true
          }));
          setShowAutoApprovalPrompt(false);
          setAutoApprovalActionType(null);
          await refreshDataForActionType(actionType);
          message.success(
            t(
              "auto_approval.enabled_success",
              "Automatic sending enabled! Configure settings in Settings > Automatic Rules."
            )
          );
        } else {
          message.error(t("auto_approval.enable_error", "Failed to enable automatic sending. Please try again."));
        }
      } catch (error) {
        console.error("Error enabling auto-approval:", error);
        message.error(t("auto_approval.enable_error", "Failed to enable automatic sending. Please try again."));
      } finally {
        setIsEnablingAutoApproval(false);
      }
    },
    [t, trackAutoApprovalEnableClick, trackAutoApprovalEnabled, refreshDataForActionType]
  );

  // Handle dismissing auto-approval prompt
  const handleDismissAutoApproval = useCallback(async () => {
    trackAutoApprovalDismissClick(autoApprovalActionType);
    if (autoApprovalActionType) {
      setDismissedAutoApproval((prev) => ({
        ...prev,
        [autoApprovalActionType]: true
      }));
    }
    setShowAutoApprovalPrompt(false);
    const actionTypeToRefresh = autoApprovalActionType;
    setAutoApprovalActionType(null);
    await refreshDataForActionType(actionTypeToRefresh);
  }, [autoApprovalActionType, trackAutoApprovalDismissClick, refreshDataForActionType]);

  // Fetch pending tasks counts (overdue and pending today)
  const fetchPendingTasks = useCallback(async () => {
    try {
      const response = await getPendingTasksCountsAPI();
      if (response?.data?.success) {
        setTasksData({
          overdueCount: response.data.data.overdueCount || 0,
          pendingTodayCount: response.data.data.pendingTodayCount || 0,
          totalCount: response.data.data.totalCount || 0
        });
      }
    } catch (error) {
      console.error("Error fetching pending tasks counts:", error);
    }
  }, []);

  // Fetch all pending actions data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchPaytientPatients(),
        fetchExpressEnrollInvitesPatients(),
        fetchPendingTasks(),
        checkAutoApprovalStatus(API_ACTION_TYPES.PAYTIENT),
        checkAutoApprovalStatus(API_ACTION_TYPES.EXPRESS_ENROLL_INVITES)
      ]);
      setIsLoading(false);
    };
    fetchAllData();
  }, [fetchPaytientPatients, fetchExpressEnrollInvitesPatients, fetchPendingTasks, checkAutoApprovalStatus]);

  // Track section impression when data is loaded
  const hasTrackedImpression = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      trackSectionImpression({
        eligiblePatientsCount: paytientData.totalCount,
        minThreshold: paytientData.minThreshold,
        expressEnrollInvitesEligibleCount: expressEnrollInvitesData.totalCount,
        expressEnrollInvitesMinThreshold: expressEnrollInvitesData.minThreshold,
        overdueTasksCount: tasksData.overdueCount,
        pendingTodayCount: tasksData.pendingTodayCount
      });
      if (paytientData.totalCount >= paytientData.minThreshold) {
        trackM3PCardImpression(paytientData.totalCount, paytientData.totalCount);
      }
      // Express Enroll Invites card impression is handled by useBoxImpressionTracking (via expressEnrollInvitesImpressionRef)
      if (tasksData.overdueCount > 0 || tasksData.pendingTodayCount > 0) {
        trackTasksCardImpression(tasksData.overdueCount, tasksData.pendingTodayCount);
      }
    }
  }, [
    isLoading,
    paytientData,
    expressEnrollInvitesData,
    tasksData,
    trackSectionImpression,
    trackM3PCardImpression,
    trackTasksCardImpression
  ]);

  // Re-fetch tasks counts when Tasks popup closes (user may have completed tasks)
  useEffect(() => {
    if (wasTasksPopupOpen && !isTasksPopupOpen) {
      const refreshTasksCounts = async () => {
        try {
          const response = await getPendingTasksCountsAPI();
          if (response?.data?.success) {
            const newOverdueCount = response.data.data.overdueCount || 0;
            const newPendingTodayCount = response.data.data.pendingTodayCount || 0;
            const newTotalCount = newOverdueCount + newPendingTodayCount;
            const previousTotalCount = tasksData.overdueCount + tasksData.pendingTodayCount;

            if (previousTotalCount > 0 && newTotalCount === 0) {
              setSuccessAnimation({
                show: true,
                actionId: ACTION_TYPES.OVERDUE_TASKS,
                count: previousTotalCount,
                label: t("pending_actions.tasks_card.tasks_completed", "tasks completed")
              });

              const timerId = setTimeout(() => {
                setTasksData({
                  overdueCount: newOverdueCount,
                  pendingTodayCount: newPendingTodayCount,
                  totalCount: newTotalCount
                });
                setSuccessAnimation({ show: false, actionId: null, count: 0, label: "" });
                message.success(t("pending_actions.tasks_card.all_completed", "Great job! All tasks completed."));
              }, 2500);
              timeoutRefs.current.push(timerId);
            } else {
              setTasksData({
                overdueCount: newOverdueCount,
                pendingTodayCount: newPendingTodayCount,
                totalCount: newTotalCount
              });
            }
          }
        } catch (error) {
          console.error("Error refreshing tasks counts:", error);
        }
      };

      refreshTasksCounts();
    }
  }, [isTasksPopupOpen, wasTasksPopupOpen, tasksData, t]);

  // Dispatch badge count for Extra Care sidebar menu item
  useEffect(() => {
    if (!isPendingActionsEnabled || isLoading) {
      dispatch(ACTIONS.setBadgeCounts("extraCare", 0));
      return;
    }
    let cardCount = 0;
    if (paytientData.totalCount >= paytientData.minThreshold && !hasActiveAutoApproval[API_ACTION_TYPES.PAYTIENT]) {
      cardCount++;
    }

    if (
      expressEnrollInvitesData.totalCount >= expressEnrollInvitesData.minThreshold &&
      !hasActiveAutoApproval[API_ACTION_TYPES.EXPRESS_ENROLL_INVITES]
    ) {
      cardCount++;
    }
    if (tasksData.overdueCount > 0 || tasksData.pendingTodayCount > 0) {
      cardCount++;
    }
    dispatch(ACTIONS.setBadgeCounts("extraCare", cardCount));
  }, [
    isPendingActionsEnabled,
    isLoading,
    paytientData,
    expressEnrollInvitesData,
    tasksData,
    hasActiveAutoApproval,
    dispatch
  ]);

  // Build action cards based on API data
  const actionCards = [];

  // Paytient M3P card
  if (paytientData.totalCount >= paytientData.minThreshold) {
    actionCards.push({
      id: ACTION_TYPES.M3P_ELIGIBLE,
      type: ACTION_TYPES.M3P_ELIGIBLE,
      title: t("pending_actions.m3p_eligible.title", "M3P Eligible Patients"),
      description: t("pending_actions.m3p_eligible.description", "Patients eligible for Medicare M3P program"),
      actionLabel: t("pending_actions.m3p_eligible.action", "Send M3P Invite"),
      count: paytientData.totalCount,
      color: PAYTIENT_PRIMARY,
      patients: paytientData.patients,
      tooltip: paytientData.tooltip
    });
  }

  // Express Enroll Invites card
  if (expressEnrollInvitesData.totalCount >= expressEnrollInvitesData.minThreshold) {
    actionCards.push({
      id: ACTION_TYPES.EXPRESS_ENROLL_INVITES_ELIGIBLE,
      type: ACTION_TYPES.EXPRESS_ENROLL_INVITES_ELIGIBLE,
      title: t("pending_actions.express_enroll_invites_eligible.title", "Patient Express Enroll (Self-Enroll)"),
      description: t("pending_actions.express_enroll_invites_eligible.description", "Patients eligible to self-enroll"),
      metricLabel: t("pending_actions.express_enroll_invites_eligible.description", "Patients eligible to self-enroll"),
      actionLabel: t("pending_actions.express_enroll_invites_eligible.action", "Send self-enroll link"),
      count: expressEnrollInvitesData.totalCount,
      color: PRIMARY_600,
      patients: expressEnrollInvitesData.patients,
      tooltip: expressEnrollInvitesData.tooltip
    });
  }

  // Check if we should show the tasks card
  const showTasksCard = tasksData.overdueCount > 0 || tasksData.pendingTodayCount > 0;
  const isTasksAnimating = successAnimation.show && successAnimation.actionId === ACTION_TYPES.OVERDUE_TASKS;

  // Shared bulk send logic used by both "Send All" and "Send Selected" flows
  const executeBulkSend = useCallback(
    async (patientIds, action) => {
      if (!patientIds?.length || !user?.id) return;

      const actionType = getApiActionType(action?.type);
      const bulkSendAPI = getBulkSendAPI(action?.type);

      trackSendInvitesClick(patientIds.length, actionType);

      try {
        setIsSending(true);
        const response = await bulkSendAPI(patientIds, SEND_SOURCES.BULK_SEND);
        const sentCount = response?.data?.data?.sentCount || 0;
        const failedCount = response?.data?.data?.failedCount || 0;

        if (response?.data?.success && sentCount > 0) {
          trackSendInvitesSuccess(sentCount, failedCount, actionType);
          setSuccessAnimation({ show: true, actionId: action?.id, count: sentCount });
          const timerId = setTimeout(() => handlePostSendSuccess({ actionType, sentCount }), 2500);
          timeoutRefs.current.push(timerId);
        } else if (sentCount === 0 && failedCount > 0) {
          message.error(t("pending_actions.send_all_failed", `Failed to send to all ${failedCount} patients.`));
        } else {
          message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
        }
      } catch (error) {
        console.error("Error in bulk send:", error);
        message.error(t("pending_actions.send_error", "Failed to send invitations. Please try again."));
      } finally {
        setIsSending(false);
      }
    },
    [t, user, trackSendInvitesClick, trackSendInvitesSuccess, handlePostSendSuccess]
  );

  const handleSendToAll = useCallback(
    (action) => {
      const patientIds = action?.patients?.map((p) => p.patientId || p.id);
      executeBulkSend(patientIds, action);
    },
    [executeBulkSend]
  );

  const handleReviewPatients = useCallback(
    (action) => {
      trackReviewPatientsClick(getApiActionType(action?.type));
      setSelectedAction(action);
      setModalVisible(true);
    },
    [trackReviewPatientsClick]
  );

  const handleViewTasks = useCallback(() => {
    trackViewTasksClick("overdue_or_pending");
    dispatch(ACTIONS.setFormPopups(true, formPopupsContentKeys.TASKS));
  }, [dispatch, trackViewTasksClick]);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedAction(null);
  }, []);

  const handleSendSelected = useCallback(
    (selectedPatients, action) => {
      setModalVisible(false);
      const patientIds = selectedPatients?.map((p) => p.patientId || p.id);
      executeBulkSend(patientIds, action);
    },
    [executeBulkSend]
  );

  // Don't render section if feature is disabled
  if (!isPendingActionsEnabled) {
    return null;
  }

  // Calculate visible cards (excluding those hidden by active auto-approval)
  const visibleActionCards = actionCards.filter((action) => {
    const actionType = getApiActionType(action.type);
    return !hasActiveAutoApproval[actionType];
  });

  // Don't render section if no cards to display (after loading completes)
  if (!isLoading && visibleActionCards.length === 0 && !showTasksCard) {
    return null;
  }

  return (
    <div className="pending-actions-section" data-pendo="pending_actions.section">
      <div className="pending-actions-header">
        <div>
          <div className="pending-actions-title">{t("pending_actions.title", "Extra Care Opportunities")}</div>
          <div className="pending-actions-subtitle">
            {t("pending_actions.subtitle", "Help more patients save on their medications")}
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : visibleActionCards.length === 0 && !showTasksCard ? (
        <EmptyState t={t} />
      ) : (
        <div className="pending-actions-grid">
          {visibleActionCards.map((action) => {
            const isAnimating = successAnimation.show && successAnimation.actionId === action.id;
            const actionType = getApiActionType(action.type);

            const shouldShowBanner = showAutoApprovalPrompt && autoApprovalActionType === actionType;

            const isExpressEnrollInvites = action.type === ACTION_TYPES.EXPRESS_ENROLL_INVITES_ELIGIBLE;

            return (
              <div
                key={action.id}
                ref={(el) => {
                  cardRefs.current[action.id] = el;
                  if (isExpressEnrollInvites) expressEnrollInvitesImpressionRef.current = el;
                }}
                className={`action-card-wrapper ${isAnimating ? "success-animation" : ""}`}
              >
                {isAnimating ? (
                  <SuccessAnimation count={successAnimation.count} />
                ) : (
                  <ActionCard
                    action={action}
                    onSendToAll={handleSendToAll}
                    onReviewPatients={handleReviewPatients}
                    onViewTasks={handleViewTasks}
                    onTooltipClick={() => trackTooltipClick(action.type, actionType)}
                    disabled={isSending}
                    showAutoApprovalBanner={shouldShowBanner}
                    onEnableAutoApproval={() => handleEnableAutoApproval(actionType)}
                    onDismissAutoApproval={handleDismissAutoApproval}
                    isEnablingAutoApproval={isEnablingAutoApproval}
                  />
                )}
              </div>
            );
          })}

          {/* Tasks Card - separate from ActionCards */}
          {(showTasksCard || isTasksAnimating) && (
            <div
              ref={(el) => (cardRefs.current[ACTION_TYPES.OVERDUE_TASKS] = el)}
              className={`action-card-wrapper ${isTasksAnimating ? "success-animation" : ""}`}
            >
              {isTasksAnimating ? (
                <SuccessAnimation
                  count={successAnimation.count}
                  label={successAnimation.label || t("pending_actions.tasks_card.tasks_completed", "tasks completed")}
                />
              ) : (
                <TasksCard
                  overdueCount={tasksData.overdueCount}
                  pendingTodayCount={tasksData.pendingTodayCount}
                  onViewTasks={handleViewTasks}
                  disabled={isSending}
                />
              )}
            </div>
          )}
        </div>
      )}

      <ReviewPatientsModal
        visible={modalVisible}
        action={selectedAction}
        onClose={handleCloseModal}
        onSendSelected={handleSendSelected}
        isSending={isSending}
      />
    </div>
  );
};

export default PendingActions;
